import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Percent, MapPin, Save, CreditCard } from "lucide-react";

export default function AdminImpostazioni() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-commission"], queryFn: async () => (await api.get("/admin/commission")).data });
  const { data: munData } = useQuery({ queryKey: ["admin-municipalities"], queryFn: async () => (await api.get("/admin/municipalities")).data });
  const [commission, setCommission] = useState("");
  const { data: pmData } = useQuery({ queryKey: ["payment-mode"], queryFn: async () => (await api.get("/admin/payment-mode")).data });
  const [pmSaving, setPmSaving] = useState(false);
  const paymentMode = pmData?.payment_mode || "off";
  const savePaymentMode = async (mode) => {
    setPmSaving(true);
    try { await api.put("/admin/payment-mode", { payment_mode: mode }); toast.success("Modalità pagamento aggiornata."); qc.invalidateQueries({ queryKey: ["payment-mode"] }); qc.invalidateQueries({ queryKey: ["public-config"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setPmSaving(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when data changes; setCommission is a stable setter
  useEffect(() => { if (data) setCommission(String(data.commission_percent)); }, [data]);

  const save = async () => {
    try { await api.put("/admin/commission", { commission_percent: Number(commission) }); toast.success("Commissione aggiornata."); qc.invalidateQueries({ queryKey: ["admin-commission"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardLayout title="Commissioni & comuni serviti">
      <div className="max-w-2xl space-y-6">
        <Card className="space-y-4">
          <div className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-[#087A73]" /><h3 className="font-bold text-[#102A2A]">Modalità di pagamento</h3></div>
          <p className="text-sm text-[#5C6B68]">Scegli come funziona la piattaforma: <strong>senza pagamento online</strong> (il paziente prenota, il professionista gestisce pagamento e fattura, nessuna commissione) oppure <strong>con pagamento online</strong> (demo/Stripe con commissione).</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <button data-testid="pm-off" disabled={pmSaving} onClick={() => savePaymentMode("off")}
              className={`text-left border-2 rounded-2xl p-4 transition-all ${paymentMode === "off" ? "border-[#087A73] bg-[#E0EFEA]" : "border-[#D2E3DB] hover:border-[#087A73]/40"}`}>
              <p className="font-bold text-[#102A2A]">Senza pagamento online</p>
              <p className="text-sm text-[#5C6B68] mt-1">Il paziente prenota, il pagamento è gestito dal professionista. Nessuna commissione.</p>
            </button>
            <button data-testid="pm-stripe" disabled={pmSaving} onClick={() => savePaymentMode("stripe")}
              className={`text-left border-2 rounded-2xl p-4 transition-all ${paymentMode === "stripe" ? "border-[#087A73] bg-[#E0EFEA]" : "border-[#D2E3DB] hover:border-[#087A73]/40"}`}>
              <p className="font-bold text-[#102A2A]">Con pagamento online</p>
              <p className="text-sm text-[#5C6B68] mt-1">Pagamento demo e Stripe (test). Si applica la commissione impostata.</p>
            </button>
          </div>
        </Card>
        {isLoading ? <LoadingBlock /> : (
          <Card className="space-y-4">
            <div className="flex items-center gap-2"><Percent className="h-5 w-5 text-[#087A73]" /><h3 className="font-bold text-[#102A2A]">Commissione piattaforma</h3></div>
            <p className="text-sm text-[#5C6B68]">Percentuale trattenuta dalla piattaforma su ogni prestazione (demo).</p>
            <div className="flex items-end gap-3">
              <div className="w-40"><Label>Percentuale (%)</Label><Input data-testid="commission-input" type="number" value={commission} onChange={(e) => setCommission(e.target.value)} className="h-12 rounded-xl mt-1" /></div>
              <Button data-testid="commission-save" onClick={save} className="bg-[#087A73] text-white rounded-xl h-12 px-6"><Save className="mr-2 h-4 w-4" /> Salva</Button>
            </div>
          </Card>
        )}
        <Card className="space-y-3">
          <div className="flex items-center gap-2"><MapPin className="h-5 w-5 text-[#087A73]" /><h3 className="font-bold text-[#102A2A]">Comuni serviti</h3></div>
          <div className="flex flex-wrap gap-2">
            {(munData?.municipalities || []).map((m) => <span key={m} className="bg-[#E0EFEA] text-[#087A73] rounded-full px-3 py-1.5 text-sm font-semibold">{m}</span>)}
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
